import { useMutation, useQuery } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import {
  LIST_USER_WORKSPACES,
  SAVE_WORKSPACE,
  DELETE_WORKSPACE,
  SET_DEFAULT_WORKSPACE,
} from '../../http/apollo-queries';
import { Button } from '../UI';
import { Checkbox } from '../form';
import { Input } from '../form';
import PopupWindow from '../Popup-window/Popup-window';
import { WorkspaceMenuPropsModel } from './types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { Loading } from '../UI';
import { IWorkspace } from '../../types/model';
import { logger } from '../../helpers/logger';

const WorkspaceManager = ({
  workspaceId,
  onCloseClick,
  initState,
}: WorkspaceMenuPropsModel) => {
  const { t } = useTranslation();
  const { user, refetch: refetchUser } = useAuth();

  logger.log('WorkspaceManager:workspaceId', workspaceId);

  const {
    loading: loadingListWorkspaces,
    data: { listWorkspaces: { items: workspaces = [] } = {} } = {},
    refetch: refetchWorkspaces,
  } = useQuery(LIST_USER_WORKSPACES, {
    variables: {
      profileId: user?.profile?.id,
    },
  });

  logger.log('WorkspaceManager:workspaces', workspaces);
  logger.log(
    'WorkspaceManager:user.profile.defaultWorkspaceId',
    user?.profile?.defaultWorkspaceId
  );

  const workspace = workspaces.find((w) => w.id === workspaceId);

  const defaultWorkspace = workspaces.find(
    (w) => w.id === user?.profile?.defaultWorkspaceId
  );

  logger.log('WorkspaceManager:defaultWorkspace', defaultWorkspace);

  const [setDefaultWorkspace, { loading: loadingSetDefaultWorkspace }] =
    useMutation(SET_DEFAULT_WORKSPACE);

  const [saveWorkspace, { loading: loadingSaveWorkspace }] =
    useMutation(SAVE_WORKSPACE);

  const [deleteWorkspace, { loading: loadingDeleteWorkspace }] =
    useMutation(DELETE_WORKSPACE);

  const [action, setAction] = useState<'manage' | 'save' | 'delete'>(initState);

  const [selectedWorkspace, setSelectedWorkspace] = useState<IWorkspace>();
  const [workspaceToDelete, setWorkspaceToDelete] = useState<IWorkspace>();

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    onSubmit: () => {},
    validateOnMount: true,
  });

  useEffect(() => {
    if (workspace?.name && workspace?.saved) {
      formik.setFieldValue('name', workspace.name);
    }
  }, [workspace?.name]);

  const handleSaveWorkspace = () => {
    const { name } = formik.values;

    logger.log('WorkspaceManager:handleSaveWorkspace', name);

    saveWorkspace({
      variables: {
        profileId: user!.profile!.id,
        workspaceId: workspace.id,
        name,
      },
    })
      .then(() => refetchWorkspaces())
      .then(() => onCloseClick())
      .catch((err) => {
        logger.error(err);
      });
  };

  const handleSaveDefaultWorkspace = () => {
    logger.log('WorkspaceManager:handleSaveWorkspace', selectedWorkspace!.id);

    setDefaultWorkspace({
      variables: {
        profileId: user!.profile!.id,
        workspaceId: selectedWorkspace!.id,
      },
    })
      .then(() => refetchUser())
      .then(() => onCloseClick())
      .catch((err) => {
        logger.error(err);
      });
  };

  const handleDeleteWorkspace = () => {
    deleteWorkspace({
      variables: {
        workspaceId: workspaceToDelete!.id,
        profileId: user!.profile!.id,
      },
    })
      .then(() => refetchWorkspaces())
      .then(() => onCloseClick())
      .catch((err) => {
        logger.error('WorkspaceManager:deleteWorkspace', err);
      });
  };

  return (
    <PopupWindow
      customPaddings="pt-14"
      className="max-h-screen-50"
      showTopNavigation
      onCloseClick={() => {
        !!onCloseClick && onCloseClick();
      }}
    >
      <Loading status={loadingListWorkspaces} />
      {action === 'save' && workspace?.id && (
        <FormikProvider value={formik}>
          <form className="w-full" autoComplete={'off'}>
            <div className="flex flex-col w-full pb-14 px-14">
              <h1 className="text-black text-xl font-semibold text-center">
                {t('workspace.saveWorkspace')}
              </h1>
              <div className="flex mt-10">
                <Input
                  key="name"
                  size="full"
                  name="name"
                  id="name"
                  formik={formik}
                  type="text"
                  placeholder="Name"
                  label="Name"
                  validate={(value: string) => {
                    return value.length === 0;
                  }}
                />
              </div>
              <div className="flex justify-center mt-14 gap-5">
                <Button
                  buttonClass="w-24"
                  type="button"
                  variant="secondary"
                  onClick={onCloseClick}
                >
                  {t('workspace.modal.cancel')}
                </Button>
                <Button
                  buttonClass="w-24"
                  type="button"
                  variant="primary"
                  loading={loadingSaveWorkspace}
                  disabled={!formik.isValid}
                  onClick={handleSaveWorkspace}
                >
                  {t('workspace.modal.save')}
                </Button>
              </div>
            </div>
          </form>
        </FormikProvider>
      )}
      {/* MANAGE WORKSPACES */}
      {action === 'manage' && workspace?.id && (
        <div className="flex flex-col w-full">
          <h1 className="text-black text-xl font-semibold text-center">
            {t('workspace.manageWorkspaces')}
          </h1>
          <div className="flex flex-col mt-10 px-14">
            <span className="font-medium text-xs mb-2 uppercase">
              {t('workspace.preset')}
            </span>
            {workspaces
              .filter((w) => w.system)
              .map((workspace) => (
                <>
                  <div
                    key={`row_wrapper_${workspace.id}`}
                    className="relative flex items-center justify-between border-b border-grey-10 py-3 pr-8"
                  >
                    <div className={'flex w-full font-normal text-sm'}>
                      {workspace.name}
                    </div>
                    <Checkbox
                      isCheck={
                        (!selectedWorkspace &&
                          workspace.id === defaultWorkspace?.id) ||
                        workspace.id === selectedWorkspace?.id
                      }
                      shape={'square'}
                      id={`default_${workspace.id}`}
                      name={`default_${workspace.id}`}
                      value={1}
                      onClick={() => {
                        setSelectedWorkspace(workspace);
                      }}
                    >
                      <span className="font-normal text-sm">
                        {t('workspace.default')}
                      </span>
                    </Checkbox>
                  </div>
                </>
              ))}
            <span className="font-medium text-xs mt-8 mb-2 uppercase">
              {t('workspace.yourWorkspaces')}
            </span>
            {workspaces
              .filter((w) => !w.system && w.saved)
              .map((workspace) => (
                <>
                  <div
                    key={`row_wrapper_${workspace.id}`}
                    className="relative flex items-center justify-between border-b border-grey-10 py-3 pr-8"
                  >
                    <div className={'flex w-full font-normal text-sm'}>
                      {workspace.name}
                    </div>
                    <Checkbox
                      id={`default_${workspace.id}`}
                      name={`default_${workspace.id}`}
                      value={1}
                      shape={'square'}
                      isCheck={
                        (!selectedWorkspace &&
                          workspace.id === defaultWorkspace?.id) ||
                        workspace.id === selectedWorkspace?.id
                      }
                      onClick={() => {
                        setSelectedWorkspace(workspace);
                      }}
                    >
                      <span className="font-normal text-sm">
                        {t('workspace.default')}
                      </span>
                    </Checkbox>
                    <div
                      className="absolute right-0 flex items-center justify-center w-5 h-5 cursor-pointer"
                      onClick={() => {
                        setWorkspaceToDelete(workspace);
                        setAction('delete');
                      }}
                    >
                      <span className="material-icons-outlined text-md font-normal text-black">
                        delete
                      </span>
                    </div>
                  </div>
                </>
              ))}
          </div>
          <div className="flex justify-center mt-14 gap-5 border-t border-grey-10 py-6">
            <Button
              buttonClass="w-24"
              type="button"
              variant="secondary"
              onClick={onCloseClick}
            >
              {t('workspace.modal.cancel')}
            </Button>
            <Button
              buttonClass="w-24"
              type="button"
              variant="primary"
              loading={loadingSetDefaultWorkspace}
              onClick={handleSaveDefaultWorkspace}
            >
              {t('workspace.modal.save')}
            </Button>
          </div>
        </div>
      )}
      {/* DELETE WORKSPACE */}
      {action === 'delete' && (
        <div className="flex flex-col w-full pb-14 px-14">
          <h1 className="text-black text-xl font-semibold text-center">
            {t('workspace.deleteWorkspaceTitle')}
          </h1>
          <h2 className="text-grey-50 text-lg font-normal text-center mt-4">
            {t('workspace.deleteWorkspaceDescription')}
          </h2>
          <div className="flex justify-center mt-10 gap-5">
            <Button
              buttonClass="w-24"
              type="button"
              variant="secondary"
              onClick={() => {
                setAction('manage');
              }}
            >
              {t('workspace.modal.cancel')}
            </Button>
            <Button
              buttonClass="w-24"
              type="button"
              variant={'negative'}
              loading={loadingDeleteWorkspace}
              onClick={handleDeleteWorkspace}
            >
              {t('workspace.modal.delete')}
            </Button>
          </div>
        </div>
      )}
    </PopupWindow>
  );
};

export default WorkspaceManager;
